/**
 * InputGroup and InputElement Components
 * Container for input with addons
 */

import React, { forwardRef, Children, cloneElement, isValidElement } from 'react';
import { Box } from '../primitives/Box';

/**
 * InputGroup Component
 * Container for Input with left/right elements
 * 
 * @example
 * <InputGroup>
 *   <InputLeftElement>
 *     <SearchIcon />
 *   </InputLeftElement>
 *   <Input placeholder="Search..." />
 *   <InputRightElement>
 *     <CloseIcon />
 *   </InputRightElement>
 * </InputGroup>
 */
export const InputGroup = forwardRef(({
  size = 'md',
  children,
  ...props
}, ref) => {
  const groupStyles = {
    position: 'relative',
    display: 'flex',
    width: '100%',
    isolation: 'isolate',
  };

  // Clone children to pass size and group context
  const clonedChildren = Children.map(children, (child) => {
    if (!isValidElement(child)) return child;

    // Check for InputLeftElement, InputRightElement, InputLeftAddon, InputRightAddon
    const childName = child.type?.displayName || child.type?.name;
    
    if (childName === 'InputLeftElement' || childName === 'InputRightElement' ||
        childName === 'InputLeftAddon' || childName === 'InputRightAddon') {
      return cloneElement(child, { size: child.props.size || size });
    }

    // Pass size to Input components
    if (childName === 'Input' || childName === 'Select' || childName === 'NumberInput') {
      return cloneElement(child, { 
        size: child.props.size || size,
      });
    }

    return child;
  });

  return (
    <Box ref={ref} {...groupStyles} {...props}>
      {clonedChildren}
    </Box>
  );
});

InputGroup.displayName = 'InputGroup';

/**
 * Shared styles for input elements
 */
const getElementStyles = (placement, size) => {
  const sizeMap = {
    xs: { width: '24px', fontSize: 'xs' },
    sm: { width: '32px', fontSize: 'sm' },
    md: { width: '40px', fontSize: 'md' },
    lg: { width: '48px', fontSize: 'lg' },
  };

  const sizeStyles = sizeMap[size] || sizeMap.md;

  return {
    position: 'absolute',
    top: 0,
    [placement]: 0,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: sizeStyles.width,
    fontSize: sizeStyles.fontSize,
    pointerEvents: 'none',
    color: 'gray.500',
  };
};

/**
 * InputLeftElement Component
 */
export const InputLeftElement = forwardRef(({
  size = 'md',
  children,
  pointerEvents,
  ...props
}, ref) => {
  const styles = getElementStyles('left', size);
  
  return (
    <Box
      ref={ref}
      {...styles}
      pointerEvents={pointerEvents || 'none'}
      {...props}
    >
      {children}
    </Box>
  );
});

InputLeftElement.displayName = 'InputLeftElement';

/**
 * InputRightElement Component
 */
export const InputRightElement = forwardRef(({
  size = 'md',
  children,
  pointerEvents,
  ...props
}, ref) => {
  const styles = getElementStyles('right', size);
  
  return (
    <Box
      ref={ref}
      {...styles}
      pointerEvents={pointerEvents || 'none'}
      {...props}
    >
      {children}
    </Box>
  );
});

InputRightElement.displayName = 'InputRightElement';

/**
 * Shared styles for input addons
 */
const getAddonStyles = (placement, size) => {
  const sizeMap = {
    xs: { px: 2, fontSize: 'xs', h: '24px' },
    sm: { px: 3, fontSize: 'sm', h: '32px' },
    md: { px: 4, fontSize: 'md', h: '40px' },
    lg: { px: 4, fontSize: 'lg', h: '48px' },
  };

  const sizeStyles = sizeMap[size] || sizeMap.md;

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    bg: 'gray.100',
    borderWidth: '1px',
    borderColor: 'gray.200',
    whiteSpace: 'nowrap',
    ...sizeStyles,
    borderRadius: placement === 'left' 
      ? '8px 0 0 8px' 
      : '0 8px 8px 0',
    [placement === 'left' ? 'borderRight' : 'borderLeft']: 'none',
  };
};

/**
 * InputLeftAddon Component
 */
export const InputLeftAddon = forwardRef(({
  size = 'md',
  children,
  ...props
}, ref) => {
  const styles = getAddonStyles('left', size);
  
  return (
    <Box ref={ref} {...styles} {...props}>
      {children}
    </Box>
  );
});

InputLeftAddon.displayName = 'InputLeftAddon';

/**
 * InputRightAddon Component
 */
export const InputRightAddon = forwardRef(({
  size = 'md',
  children,
  ...props
}, ref) => {
  const styles = getAddonStyles('right', size);
  
  return (
    <Box ref={ref} {...styles} {...props}>
      {children}
    </Box>
  );
});

InputRightAddon.displayName = 'InputRightAddon';

export default InputGroup;
